import React from 'react';

export interface ErrorStateProps {
  /** Heading copy. Falls back to a generic "Couldn't load results". */
  readonly heading?: string;
  /** Body copy. Falls back to a generic apology. */
  readonly body?: string;
  /** Optional retry CTA label. When omitted no button renders. */
  readonly ctaLabel?: string;
  /**
   * Optional retry handler. Today the repeater forwards whatever
   * `onRetry` the author wires through `DataRepeater` props directly
   * — there is no `_action.onRetry` dispatcher yet and the DSM does
   * not surface a per-source refetcher (see follow-up tickets in
   * `output/EDUN-10693/build.md`). This stays optional so the CTA
   * is omitted when no retry path is available.
   */
  readonly onRetry?: () => void;
}

/**
 * Rendered when the iterated source's status is `'error'`. Heading,
 * body, and CTA copy all come from the props passed in by the
 * `DataRepeater` (`errorStateHeading`, `errorStateBody`,
 * `errorCtaLabel`). The SAD's `dataSource.errorStrategy.fallback`
 * shape is intentionally NOT consumed here yet — that contract was
 * deferred so the repeater could ship without a DSM API change. See
 * follow-up tickets in `output/EDUN-10693/build.md`.
 *
 * Accessibility:
 * - `role="alert"` announces the error to screen readers immediately.
 * - The CTA is a real `<button>` so it inherits keyboard semantics.
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  heading = "Couldn't load results",
  body = 'Please try again in a moment.',
  ctaLabel,
  onRetry,
}) => (
  <div
    role='alert'
    data-testid='data-repeater-error'
    style={{ padding: 24, textAlign: 'center' }}
  >
    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{heading}</h3>
    <p style={{ marginTop: 8, marginBottom: 0, color: 'rgba(0,0,0,0.65)' }}>
      {body}
    </p>
    {ctaLabel && onRetry ? (
      <button
        type='button'
        onClick={onRetry}
        style={{
          marginTop: 16,
          padding: '8px 16px',
          borderRadius: 6,
          border: '1px solid currentColor',
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        {ctaLabel}
      </button>
    ) : null}
  </div>
);
